/**
 * What to hunt, and where to stand for it (docs/spec/02 §5-§8; 03 §2, §5). Every enemy that a shot
 * could reach within the horizon is priced: what it is worth where it will be, less the wait. Divers
 * and arrivals beat the formation, a Monarch carrying a captive beats everything (that is the
 * rescue), and a capsule about to be lost beats the hunt.
 */

import * as math from '../sim/math';
import * as sim from '../sim';
import type { Enemy, GameState, Rng } from '../sim';
import type { Forecast } from './forecast';
import * as gunnery from './gunnery';
import type { Gunnery } from './gunnery';
import * as horizon from './horizon';

// Local copies (see horizon.ts).
const { clamp } = math;
const { FIELD, FIGHTER, NOVA } = sim;
const { EMPRESS, NOBODY } = gunnery;
const { HORIZON, MUZZLE_Y, OFF, STRIDE } = horizon;

/** What a tick of waiting for a hit costs, in points. */
const WAIT = 4;
/** The target in hand is worth this much more than a new one: no dithering between equals. */
const STICK = 24;
const RESCUE = 2000;
const EMPRESS_WORTH = 300;
/** A target worth this or more has the last free volley kept for it. */
const PRIZE = 300;
/** Start for a capsule when it is this many ticks from needing the ship under it. */
const CAPSULE_LEAD = 30;
/** The Empress's id in the bot's memory (enemy ids start at 1). */
const EMPRESS_KEY = -2;
/** How far off its middle a seated enemy, and the Empress, may be aimed at. */
const SEAT_TOLERANCE = 4;
const EMPRESS_TOLERANCE = NOVA.empressHalfW - 8;
/** A capsule is taken within this of the fighter's row (the engine's `updateCapsules`). */
const CAPSULE_REACH = NOVA.capsuleHalf + FIGHTER.hitH;

/** What `e` is worth to shoot; negative for what must not be shot. */
function worth(s: GameState, e: Enemy, seated: boolean): number {
  if (e.kind === 'captive') return -1;
  if (s.phase === 'challenge') return 100;
  switch (e.kind) {
    case 'monarch':
      // Killed at home its captive is lost; killed in flight the captive comes back (02 §6).
      if (e.partner >= 0) return seated ? -1 : RESCUE;
      return seated ? 60 : 400;
    case 'moth': return seated ? 60 : 160;
    case 'morph': return 160;
    default: return seated ? 50 : 100;
  }
}

export class Targeting {
  /** Where the ship should be. */
  goal = FIELD.w / 2;
  /** The target: its index in `state.enemies` (or `EMPRESS`, or `NOBODY`). */
  mark = NOBODY;
  /** The tick the shot at it is due to leave. */
  fireAt = 0;
  /** It is worth keeping the last free volley for. */
  prize = false;

  /** The target from tick to tick: an enemy's id, `EMPRESS_KEY`, or 0. */
  private key = 0;
  private salt = 0;
  private best = 0;
  private bestKey = 0;
  /** The ship, and the candidate being measured: where to stand, when the shot lands and leaves. */
  private x = 0;
  private lo = 0;
  private hi = 0;
  private aim = 0;
  private hitAt = 0;
  private shootAt = 0;

  /**
   * For a ship (or pair) at `x` that may be anywhere from `lo` to `hi`. `slip`: how many ticks out
   * the bot's eye is for things in flight.
   */
  choose(s: GameState, f: Forecast, gun: Gunnery, x: number, lo: number, hi: number, slip: number, rng: Rng): void {
    const speed = FIGHTER.shotSpeed * (s.powers.rapid > 0 ? NOVA.rapidShot : 1);
    const offset = s.player.dual ? FIGHTER.dualOffset : 0;
    const t = f.tracks;
    this.x = x;
    this.lo = lo;
    this.hi = hi;
    this.best = -Infinity;
    this.bestKey = 0;
    this.mark = NOBODY;
    this.prize = false;
    this.goal = FIELD.w / 2;
    for (let r = 0; r < t.count; r++) {
      const i = t.who[r] ?? NOBODY;
      const e = s.enemies[i];
      if (!e || (gun.pending[i] ?? 0) >= e.hp) continue;
      const w = worth(s, e, false);
      if (w >= 0 && this.track(f, r, speed, offset, slip)) this.offer(w, e.id, i);
    }
    for (let col = 0; f.seated > 0 && col <= 9; col++) {
      // A captive rides at the top of this column: nothing is to be fired up it (see gunnery.ts).
      if (f.seat(-1, col) >= 0 || (s.enemies[f.seat(0, col)]?.partner ?? -1) >= 0) continue;
      // The lowest in the column that no shot is already on its way to.
      for (let row = 4; row >= 0; row--) {
        const i = f.seat(row, col);
        const e = s.enemies[i];
        if (!e || (gun.pending[i] ?? 0) >= e.hp) continue;
        const w = worth(s, e, true);
        if (w >= 0 && this.seat(f, row, col, speed, offset)) this.offer(w, e.id, i);
        break;
      }
    }
    if (f.empress && this.empress(f, speed)) this.offer(EMPRESS_WORTH, EMPRESS_KEY, EMPRESS);
    if (this.bestKey !== this.key) {
      this.key = this.bestKey;
      this.salt = rng.next();
    }
    this.capsule(s);
    // A rescued ship on its way down docks beside the fighter: stand still and let it (02 §6).
    if (s.rescue !== null && s.rescue.phase === 'fly' && !s.player.dual) {
      this.goal = x;
      this.prize = false;
    }
  }

  /** Keep the candidate just measured if it beats the best so far. */
  private offer(w: number, key: number, index: number): void {
    // A small taste of its own, the same from tick to tick, settles which of several as good.
    const taste = Math.imul(key + this.salt, 0x9e3779b1) >>> 29;
    const score = w - WAIT * this.hitAt + (key === this.key ? STICK : 0) + taste;
    if (score <= this.best) return;
    this.best = score;
    this.bestKey = key;
    this.mark = index;
    this.goal = this.aim;
    this.fireAt = this.shootAt;
    this.prize = w >= PRIZE;
  }

  /**
   * Can a shot fired on tick `at` reach something at `ex`, `tol` either side? The ship has `at`
   * moves to get a gun under it. Sets `aim`.
   */
  private reach(ex: number, at: number, offset: number, tol: number): boolean {
    const x = this.x;
    // With two guns, the nearer one.
    const left = Math.abs(ex - offset - x) < Math.abs(ex + offset - x);
    const centre = left ? ex - offset : ex + offset;
    const stand = clamp(centre, this.lo, this.hi);
    if (Math.abs(stand - centre) + Math.max(0, Math.abs(stand - x) - FIGHTER.speed * at) > tol) return false;
    this.aim = stand;
    return true;
  }

  /** The soonest a shot could meet track `r`. */
  private track(f: Forecast, r: number, speed: number, offset: number, slip: number): boolean {
    const t = f.tracks;
    const base = r * STRIDE;
    const tol = (t.half[r] ?? 6) - 2;
    for (let k = 1; k <= HORIZON; k++) {
      const seen = base + Math.max(0, Math.min(HORIZON, k + slip));
      const y = t.y[seen] ?? OFF;
      if (y <= 4 || y > MUZZLE_Y) continue;
      const at = k - Math.max(1, Math.round((MUZZLE_Y - y) / speed)) + 1;
      if (at < 1 || !this.reach(t.x[seen] ?? 0, at, offset, tol)) continue;
      this.hitAt = k;
      this.shootAt = at;
      return true;
    }
    return false;
  }

  /** The soonest a shot could meet whoever sits at (row, col). */
  private seat(f: Forecast, row: number, col: number, speed: number, offset: number): boolean {
    const flight = Math.max(1, Math.round((MUZZLE_Y - f.slotY(0, row)) / speed));
    for (let k = flight; k <= HORIZON; k++) {
      const at = k - Math.max(1, Math.round((MUZZLE_Y - f.slotY(k, row)) / speed)) + 1;
      if (at < 1 || !this.reach(f.slotX(k, col), at, offset, SEAT_TOLERANCE)) continue;
      this.hitAt = k;
      this.shootAt = at;
      return true;
    }
    return false;
  }

  /** She is wide and slow: stand under where she will be when the shot arrives. */
  private empress(f: Forecast, speed: number): boolean {
    for (let k = 1; k <= HORIZON; k++) {
      const under = (f.ey[k] ?? 0) + NOVA.empressHalfH - 4;
      const at = k - Math.max(1, Math.round((MUZZLE_Y - under) / speed)) + 1;
      if (at < 1 || !this.reach(f.ex[k] ?? 0, at, 0, EMPRESS_TOLERANCE)) continue;
      this.hitAt = k;
      this.shootAt = at;
      return true;
    }
    return false;
  }

  /** A capsule that will be lost unless the ship goes now takes over the goal (03 §2). */
  private capsule(s: GameState): void {
    let soonest = Infinity;
    for (const c of s.capsules) {
      // Ticks until it is low enough to take, and until it is too low.
      const first = FIELD.fighterY - CAPSULE_REACH - c.y;
      const last = FIELD.fighterY + CAPSULE_REACH - c.y;
      const stand = clamp(c.baseX, this.lo, this.hi);
      const need = Math.ceil(Math.max(0, Math.abs(stand - this.x) - 4) / FIGHTER.speed);
      if (need > last || first - need > CAPSULE_LEAD || last >= soonest) continue;
      soonest = last;
      this.goal = stand;
      this.prize = false;
    }
  }
}
